import { hideOnError } from "@/lib/img-fallback";
import { createFileRoute } from "@tanstack/react-router";

import { Layout } from "@/components/Layout";
import { Reveal } from "@/components/Reveal";
import { CountUp } from "@/components/CountUp";
import { lazySection } from "@/lib/lazy-section";
import { BookingCtaButton } from "@/components/cta/BookingCtaButton";
import { ContactFaqSection, type Faq } from "@/components/contact/ContactFaqSection";
import {
  testimonialsQueryOptions,
  casesByTagQueryOptions,
  leadMagnetTeaserQueryOptions,
} from "@/lib/public-content.queries";

// Häufige Fragen: dieselbe Liste speist den Abschnitt und das FAQPage-Schema.
const BUERGERMEISTER_FAQS: Faq[] = [
  {
    q: "Was kostet die Begleitung einer Bürgermeisterwahl?",
    a: "Das hängt von der Größe Ihrer Gemeinde, dem Zeitraum bis zur Wahl und dem gewünschten Umfang ab. Unsere Leistungen sind modular buchbar, Sie zahlen also nur, was Ihr Wahlkampf tatsächlich braucht. Einzelne Bausteine beginnen bereits bei rund 1.000 Euro. In der kostenlosen Erstberatung nennen wir Ihnen eine realistische Größenordnung für Ihre Situation.",
  },
  {
    q: "Was passiert in der kostenlosen Erstberatung?",
    a: "In 30 Minuten per Video sprechen Sie mit Simon Mai über Ihre Ausgangslage. Sie erhalten eine ehrliche Einschätzung Ihrer Chancen, erste strategische Ansätze und eine Orientierung zu Ablauf, Zeitplan und Budget. Die Beratung ist kostenlos und unverbindlich.",
  },
  {
    q: "Bleibt meine Kandidatur vertraulich?",
    a: "Ja. Viele Kandidatinnen und Kandidaten sprechen mit uns, bevor sie ihre Bewerbung öffentlich machen. Jede Anfrage und jedes Gespräch behandeln wir streng vertraulich.",
  },
  {
    q: "Wann sollte ich mit der Vorbereitung beginnen?",
    a: "Je früher, desto mehr Spielraum haben Sie für Strategie, Personenmarke und Werbemittel. Aber auch kurzfristige Kandidaturen begleiten wir erfolgreich: Einige unserer Kampagnen sind innerhalb weniger Tage entstanden. Für solche Fälle haben wir ein eigenes Express-Angebot.",
  },
  {
    q: "Arbeiten Sie nur für bestimmte Parteien?",
    a: "Nein. Wir sind überparteilich und begleiten Kandidatinnen und Kandidaten aller demokratischen Parteien ebenso wie Parteilose. Für Personen oder Gruppen, die die Demokratie infrage stellen, arbeiten wir nicht.",
  },
  {
    q: "Muss ich ein Komplettpaket buchen?",
    a: "Nein. Sie können uns für einzelne Bausteine beauftragen, etwa Strategie, Kampagnendesign oder Social Media, oder uns den gesamten Wahlkampf vom ersten Strategiegespräch bis zum Wahltag anvertrauen.",
  },
  {
    q: "Wer ist während des Wahlkampfs mein Ansprechpartner?",
    a: "Sie haben einen festen Ansprechpartner, der Ihre Kampagne kennt und per Telefon, E-Mail und WhatsApp erreichbar ist. Ein Ticketsystem oder Callcenter gibt es bei uns nicht.",
  },
  {
    q: "Arbeiten Sie auch in meiner Region?",
    a: "Wir begleiten Wahlkämpfe in ganz Deutschland. Neben unserem Sitz in Berlin haben wir Regionalbüros im Rhein-Main-Gebiet und in Baden-Württemberg. Für Foto- und Videoaufnahmen oder Workshops kommen wir zu Ihnen.",
  },
  {
    q: "Wie erfolgreich sind Ihre Kandidatinnen und Kandidaten?",
    a: "72,6 Prozent der von uns begleiteten Kandidatinnen und Kandidaten haben ihre Bürgermeisterwahl gewonnen, darunter Parteilose, Bewerber von außen und Herausforderer gegen langjährige Amtsinhaber.",
  },
];

// Below-the-Fold: nach Hydration nachladen (Daten via Loader-Prefetch im Cache).
const VoteBoxAnimation = lazySection(async () => {
  const m = await import("@/components/buergermeisterwahl/VoteBoxAnimation");
  return { default: m.VoteBoxAnimation };
}, { minHeight: 360 });
const TestimonialsSlider = lazySection(async () => {
  const m = await import("@/components/home/TestimonialsSlider");
  return { default: m.TestimonialsSlider };
}, { minHeight: 520 });
const CasesSection = lazySection(async () => {
  const m = await import("@/components/cases/CasesSection");
  return { default: m.CasesSection };
}, { minHeight: 480 });
const ContactCta = lazySection(async () => {
  const m = await import("@/components/buergermeisterwahl/ContactCta");
  return { default: m.ContactCta };
}, { minHeight: 400 });
// Ticket W14: Freebie-Abschnitt – wiederverwendbar, unterhalb des Sichtfelds.
const FreebieTeaserSection = lazySection(async () => {
  const m = await import("@/components/freebie/FreebieTeaserSection");
  return { default: m.FreebieTeaserSection };
}, { minHeight: 320 });
const ProzessTimeline = lazySection(async () => {
  const m = await import("@/components/buergermeisterwahl/ProzessTimeline");
  return { default: m.ProzessTimeline };
}, { minHeight: 600 });
import michaelBusserAsset from "@/assets/parteikampagnen/michael-busser.png.asset.json";
import fotografinAsset from "@/assets/parteikampagnen/fotografin.png.asset.json";
import analyseAsset from "@/assets/parteikampagnen/analyse-strategie-illu.svg.asset.json";
import designAsset from "@/assets/design-2.svg.asset.json";
import socialMediaAsset from "@/assets/social-media-v2.webp.asset.json";
import presseMedienarbeitAsset from "@/assets/buergermeisterwahl/presse-medienarbeit.webp.asset.json";
import teamRioAsset from "@/assets/parteikampagnen/team-rio.png.asset.json";
import beratungAsset from "@/assets/beratung.webp.asset.json";
import einzigartigeErfahrungAsset from "@/assets/buergermeisterwahl/einzigartige-erfahrung.png.asset.json";
import direktmandatZuUnsAsset from "@/assets/buergermeisterwahl/direktmandat-zu-uns.png.asset.json";
import keineLieblingsparteiAsset from "@/assets/buergermeisterwahl/keine-lieblingspartei.png.asset.json";
import modularUndFairAsset from "@/assets/buergermeisterwahl/modular-und-fair.png.asset.json";
import sicherheitDurchErfahrungAsset from "@/assets/buergermeisterwahl/sicherheit-durch-erfahrung.png.asset.json";



export const Route = createFileRoute("/wahlen-gewinnen/buergermeisterwahl")({
  // Ticket W7: Herkunftskennzeichnung vom Verteiler /wahlkampfagentur (?von=...).
  validateSearch: (search: Record<string, unknown>): { von?: string } => ({
    von: typeof search.von === "string" ? search.von : undefined,
  }),
  // Testimonials und Cases speisen nur lazy nachgeladene Below-the-Fold-Sektionen
  // → Prefetch anstoßen, aber nicht awaiten (kürzere TTFB / kritische Kette).
  loader: async ({ context }) => {
    void context.queryClient.prefetchQuery(testimonialsQueryOptions);
    void context.queryClient.prefetchQuery(casesByTagQueryOptions("buergermeisterwahl"));
    void context.queryClient.prefetchQuery(
      leadMagnetTeaserQueryOptions("musterrede-buergermeisterwahl"),
    );
  },
  head: () => {
    const title = "Bürgermeisterwahl gewinnen | creategy";
    const description =
      "Marktführer für erfolgreiche Bürgermeisterwahlen: Strategie, Kampagne und Design aus einer Hand. 72,6 Prozent Erfolgsquote. Jetzt Erstgespräch sichern.";
    const url = "https://creategy.de/wahlen-gewinnen/buergermeisterwahl";
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "website" },
        { property: "og:url", content: url },
        { property: "og:site_name", content: "creategy" },
        { property: "og:locale", content: "de_DE" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: description },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: BUERGERMEISTER_FAQS.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          }),
        },
      ],
    };
  },
  component: BuergermeisterwahlPage,
});

function BuergermeisterwahlPage() {
  const gruende = [
    {
      title: "Sicherheit durch Erfahrung.",
      text: "Über 200 Bürgermeisterwahlen, 72,6 Prozent Erfolgsquote, kein Auftrag, der nicht politisch ist. Wer zu uns kommt, bekommt kein Team, das sich einarbeitet, sondern eines das schon da war.",
    },
    {
      title: "Modular und fair.",
      text: "Sie buchen nur, was Sie brauchen. Kein aufgeblähtes Paket, keine versteckten Kosten, kein Upselling im falschen Moment. Transparentes Pricing, das zu Ihrer Gemeinde und Ihrem Budget passt.",
    },
    {
      title: "Wir haben keine Lieblingspartei.",
      text: "Aber wir haben eine Lieblingsidee: Demokratie. Wir arbeiten für alle, die demokratisch antreten, egal welche Farbe. Aber für niemanden, der den Wettbewerb selbst abschaffen will.",
    },
    {
      title: "Einzigartige Erfahrung.",
      text: "Unser Senior Advisor war Regierungssprecher und Staatssekretär a.D., weiß wie Kommunikation in den schwierigsten Momenten funktioniert und gibt dieses Wissen direkt an unsere Kandidatinnen und Kandidaten weiter.",
    },
    {
      title: "Ihr Direktmandat zu uns.",
      text: 'Kein Ticketsystem, keine Warteschleife, kein „Ich leite das weiter." Bei creategy haben Sie einen festen Ansprechpartner: erreichbar per Telefon, Mail und WhatsApp.',
    },
  ];

  const gruendeImages = [
    { src: sicherheitDurchErfahrungAsset.url, alt: "Strategieentwicklung auf Whiteboard als Symbol für Erfahrung" },
    { src: modularUndFairAsset.url, alt: "Gelber Baustein wird auf eine graue Mauer gesetzt – Symbol für modulare Bausteine" },
    { src: keineLieblingsparteiAsset.url, alt: "Stimmzettel und Briefwahlumschlag – Symbol für parteiliche Unabhängigkeit" },
    { src: einzigartigeErfahrungAsset.url, alt: "Senior Advisor Michael Bußer im Gespräch" },
    { src: direktmandatZuUnsAsset.url, alt: "Persönlicher Ansprechpartner am Telefon" },
  ];






  return (
    <Layout>
      {/* Hero */}
      <section aria-labelledby="hero">
        <div className="section-y-hero mx-auto w-full max-w-[1400px] px-6 md:px-12">
          <div className="grid gap-12 md:grid-cols-2 md:items-center">
            <div className="relative">
              <h1
                id="hero"
                className="heading-h1 relative z-10 text-brand-wahlen md:w-[145%]"
              >
                Deutschlands{"\u00a0"}
                <br />
                Marktführer{"\u00a0"}für
                <br />
                Bürgermeister{"\u00AD"}wahlen.
              </h1>
              <p
                className="fade-in-up mt-10 font-body text-base leading-relaxed text-foreground md:text-lg"
                style={{ animationDelay: "160ms" }}
              >
                creategy ist der Marktführer für erfolgreiche Bürgermeister- und
                Oberbürgermeisterwahlen. Seit 2021 begleiten wir Kandidatinnen
                und Kandidaten in der ganzen Republik.
              </p>
              <BookingCtaButton
                variant="primary"
                className="fade-in-up mt-10 max-w-max"
                style={{ animationDelay: "240ms" }}
                eventPath="creategy/erstgespraech-bm"
              />
            </div>

            {/* Vote-Box Scroll-Animation */}
            <div className="order-first md:order-none w-full max-w-[240px] sm:max-w-[280px] md:max-w-[360px] mx-auto md:ml-auto md:mr-0 lg:max-w-[420px]">
              <VoteBoxAnimation />
            </div>
          </div>

        </div>
      </section>

      {/* Kennzahlen */}
      <section aria-labelledby="kennzahlen">
        <div className="section-y mx-auto w-full max-w-[1400px] px-6 md:px-12">
          <dl className="grid gap-10 sm:grid-cols-2 md:grid-cols-3">

            <div className="flex flex-col">
              <dd className="font-display text-5xl font-extrabold leading-none text-brand-wahlen md:text-6xl">
                <CountUp end={72.6} decimals={1} suffix=" %" />
              </dd>
              <dt className="mt-4 max-w-xs font-body text-sm leading-snug text-foreground">
                Erfolgsquote bei Bürgermeisterwahlen – bundesweit und über alle Gemeindegrößen.
              </dt>
            </div>
            <div className="flex flex-col">
              <dd className="font-display text-5xl font-extrabold leading-none text-brand-wahlen md:text-6xl">
                <CountUp end={200} suffix="+" />
              </dd>
              <dt className="mt-4 max-w-xs font-body text-sm leading-snug text-foreground">
                Betreute Wahlkämpfe in der ganzen Republik. Von der kleinen Gemeinde bis zur Großstadt.
              </dt>
            </div>
            <div className="flex flex-col">
              <dd className="font-display text-5xl font-extrabold leading-none text-brand-wahlen md:text-6xl">
                <CountUp end={7} suffix=" Tage" />
              </dd>
              <dt className="mt-4 max-w-xs font-body text-sm leading-snug text-foreground">
                Express-Setup für kurzfristige Kandidaturen. Weil Wahltermine keine Rücksicht auf Vorlaufzeiten nehmen.
              </dt>
            </div>
          </dl>
        </div>
      </section>

      {/* Bausteine */}
      <section aria-labelledby="bausteine">
        <div className="section-y mx-auto w-full max-w-[1400px] px-6 md:px-12">
          <Reveal>
            <h2
              id="bausteine"
              className="heading-h2 text-brand-wahlen"
            >
              Die Bausteine{" "}
              Ihres Wahlerfolgs.
            </h2>
          </Reveal>
          <Reveal>
            <p className="mt-8 font-body text-base leading-relaxed text-foreground md:text-lg">
              Kein Wahlkampf ist wie der andere – deshalb buchen Sie bei uns nur, was Sie wirklich brauchen.
              Alle Bausteine kommen aus einer Hand, greifen ineinander und lassen sich flexibel kombinieren.&nbsp;
            </p>
          </Reveal>
          <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-6">
            {([
              { title: "Analyse & Strategie", text: "Das Fundament Ihrer Kampagne: Wir analysieren Ihre Gemeinde, Ihre Ausgangslage und Ihre Mitbewerber und entwickeln daraus eine Strategie, die zu Ihnen und Ihrer Gemeinde passt.", rightBottomImage: analyseAsset.url, rightBottomImageAlt: "Illustration: Analyse & Strategie" },
              { title: "Design", text: "Für den ersten Eindruck gibt es keine zweite Wahl. Wir entwickeln ein individuelles Kampagnendesign, das zu Ihnen passt und im Gedächtnis bleibt. Von der Personenmarke bis zum letzten Werbemittel.", image: designAsset.url, imageAlt: "Illustration als Symbol für Kampagnendesign", imageSize: "sm" as const },
              { title: "Presse- & Medienarbeit", text: "Lokale Medien sind im Bürgermeisterwahlkampf entscheidend. Wir entwickeln Pressemitteilungen, koordinieren Interviews und sorgen dafür, dass Ihre Botschaft ankommt.", image: presseMedienarbeitAsset.url, imageAlt: "Hände halten Tageszeitungen und ein Mikrofon – Symbol für Presse- und Medienarbeit", flushBottom: true },
              { title: "Rhetorik- & Medientraining", text: "Die Kandidatenvorstellung entscheidet oft mehr als vier Wochen Wahlkampf. Regierungssprecher a.D. Michael Bußer bereitet Sie auf genau diesen großen Moment vor.", leftBottomImage: michaelBusserAsset.url, leftBottomImageAlt: "Michael Bußer, Regierungssprecher a.D., beim Rhetorik- und Medientraining" },
              { title: "Beratung", text: "Ein fester Ansprechpartner, der antwortet. Wir begleiten Sie durch den gesamten Wahlkampf: Strategisch, operativ und in den Momenten, in denen es keine einfache Antwort gibt.", image: beratungAsset.url, imageAlt: "Berater im Gespräch am Telefon – Symbol für persönliche Begleitung und strategische Beratung im Wahlkampf" },
              { title: "Foto- & Videoproduktion", text: "Ein Bild sagt mehr als tausend Worte. Wir produzieren professionelles Bildmaterial und Kampagnenvideos, die Ihre Botschaft transportieren und auf allen Kanälen funktionieren.", rightBottomImage: fotografinAsset.url, rightBottomImageAlt: "Fotografin mit Kamera und Softbox bei einer professionellen Kampagnenproduktion" },
              { title: "Social Media", text: "Wahlkampf findet heute auch im Feed statt. Wir richten Ihre Kanäle ein und befüllen Sie, damit Sie auch die erreichen, die kein Plakat mehr lesen.", coverImage: socialMediaAsset.url, coverImageAlt: "Haufen roter Like- und Reaktions-Icons als Symbol für Social-Media-Engagement im Wahlkampf" },
              { title: "Website", text: "Ihre digitale Visitenkarte: suchmaschinenoptimiert, mobilfreundlich und auf Ihren Wahltermin ausgerichtet. Inkl. technischer Wartung bis zum Wahltag.", image: teamRioAsset.url, imageAlt: "Rio (creategy) am Laptop – Symbol für operative Website-Beratung" },
            ] as Array<{ title: string; text: string; image?: string; imageAlt?: string; imageSize?: "sm"; inlineImage?: string; inlineImageAlt?: string; coverImage?: string; coverImageAlt?: string; rightBottomImage?: string; rightBottomImageAlt?: string; leftBottomImage?: string; leftBottomImageAlt?: string; flushBottom?: boolean }>).map((b, i) => {
              const span = i < 2 ? "lg:col-span-3" : "lg:col-span-2";
              return (
                <Reveal key={b.title} delay={i * 60} className={`${span} h-full`}>
                  <article className="flex h-[20rem] flex-row overflow-hidden rounded-md bg-brand-wahlen text-background md:h-[24rem]">
                    {b.leftBottomImage || b.rightBottomImage ? (
                      <div className="flex flex-1 flex-col">
                        <div className="px-6 pt-6 md:px-8 md:pt-8">
                          <h3 className="heading-h3 text-background">
                            {b.title}
                          </h3>
                        </div>
                        <div className="mt-4 flex min-h-0 flex-1 flex-row items-stretch gap-4 md:gap-6">
                          {b.leftBottomImage ? (
                            <>
                              <div className="relative w-[45%] shrink-0 self-stretch">
                                <img
                                  src={b.leftBottomImage}
                                  alt={b.leftBottomImageAlt ?? ""}
                                  className="absolute inset-0 block h-full w-full object-contain object-left-bottom"
                                  decoding="async" onError={hideOnError}
                                />
                              </div>
                              <p className="min-w-0 flex-1 pb-6 pr-6 font-body text-sm leading-relaxed text-background hyphens-auto-de text-pretty md:pb-8 md:pr-8">
                                {b.text}
                              </p>
                            </>
                          ) : (
                            <>
                              <p className="min-w-0 flex-1 pb-6 pl-6 font-body text-sm leading-relaxed text-background hyphens-auto-de text-pretty md:pb-8 md:pl-8">
                                {b.text}
                              </p>
                              <div className="relative w-[45%] shrink-0 self-stretch">
                                <img
                                  src={b.rightBottomImage}
                                  alt={b.rightBottomImageAlt ?? ""}
                                  className="absolute inset-0 block h-full w-full object-contain object-right-bottom"
                                  decoding="async" onError={hideOnError}
                                />
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="flex flex-1 flex-col p-6 md:p-8">
                        <h3 className="heading-h3 text-background">
                          {b.title}
                        </h3>
                        <p className="mt-4 font-body text-sm leading-relaxed text-background hyphens-auto-de text-pretty">
                          {b.text}
                        </p>
                        {b.inlineImage ? (
                          <div className="mt-6 flex justify-center">
                            <img
                              src={b.inlineImage}
                              alt={b.inlineImageAlt ?? ""}
                              className="block h-auto w-auto max-h-[7rem] max-w-full object-contain md:max-h-[10rem]"
                              decoding="async" onError={hideOnError}
                            />
                          </div>
                        ) : null}
                        {b.image ? (
                          <div className={`-mx-6 mt-auto flex min-h-0 flex-1 items-end justify-center md:-mx-8 ${b.flushBottom ? "-mb-6 md:-mb-8" : "-mb-10 md:-mb-12"}`}>
                            <img
                              src={b.image}
                              alt={b.imageAlt ?? ""}
                              className={`block h-auto w-auto max-w-full object-contain object-bottom ${b.imageSize === "sm" ? "max-h-[9.5rem] md:max-h-[12rem]" : "max-h-[11rem] md:max-h-[14rem]"}`}
                              decoding="async" onError={hideOnError}
                            />
                          </div>
                        ) : b.coverImage ? (
                          <div className="-mx-6 -mb-10 mt-auto flex min-h-0 flex-1 items-end overflow-hidden max-h-[12rem] md:-mx-8 md:-mb-12 md:max-h-[16rem]">
                            <img
                              src={b.coverImage}
                              alt={b.coverImageAlt ?? ""}
                              className="block h-full w-full object-cover object-top"
                              decoding="async" onError={hideOnError}
                            />
                          </div>

                        ) : b.inlineImage ? null : (
                          <div
                            aria-hidden="true"
                            className="mt-auto flex aspect-[4/3] items-center justify-center rounded-md border border-dashed border-border bg-secondary/40"
                          >
                            <span className="font-display text-xs uppercase tracking-wider text-muted-foreground">
                              Bild folgt
                            </span>
                          </div>
                        )}
                      </div>
                    )}
                  </article>
                </Reveal>
              );
            })}
          </div>

        </div>
      </section>

      {/* Cases */}
      <CasesSection
        tag="buergermeisterwahl"
        showPlaceholders
        headingColor="text-brand-wahlen"
        bordered={false}
        showCta
        pinnedSlugs={[
          "dr-fabian-geyer",
          "susen-katter",
          "otto-ruppaner",
          "nikolas-kopp",
          "martin-cremer-dortmunds-beste-kampagne",
        ]}
      />

      {/* 5 Gründe für creategy – Sticky-Stack der Karten, Bild links parallel sticky */}
      <section
        aria-labelledby="gruende"
        className="relative z-0 isolate bg-background text-foreground"
      >
        <div className="section-y mx-auto w-full max-w-[1400px] px-6 md:px-12">
          <div className="mb-6 md:mb-10">
            <Reveal>
              <h2
                id="gruende"
                className="heading-h2 text-brand-wahlen"
              >
                5 Gründe{" "}
                für creategy
              </h2>
            </Reveal>
            <Reveal delay={80}>
              <p className="mt-4 font-body text-sm leading-relaxed text-foreground/85 md:text-base">
                Warum Kandidatinnen und Kandidaten sich für creategy entscheiden:
                fünf Gründe, die im Wahlkampf den Unterschied machen.
              </p>
            </Reveal>
          </div>

          <ol className="flex flex-col gap-4 md:gap-0">
            {gruende.map((card, i) => {
              const img = gruendeImages[i];
              return (
                <li
                  key={card.title}
                  className="md:sticky md:top-32 md:pb-[6.66vh]"
                  style={{ zIndex: 10 + i }}
                >
                  <article className="grid gap-8 rounded border border-brand-wahlen/20 bg-background p-7 shadow-[0_10px_30px_-12px_rgba(0,75,81,0.18)] md:min-h-[28rem] md:grid-cols-[minmax(0,1.5fr)_minmax(0,3.5fr)] md:items-stretch md:gap-8 md:p-9 lg:min-h-0">
                    {/* Bild-Slot links */}
                    <div className="relative aspect-square w-full max-w-[360px] overflow-hidden rounded border border-brand-wahlen/20 bg-brand-wahlen/5">
                      <img
                        src={img.src}
                        alt={img.alt}
                        className="h-full w-full object-cover"
                        loading="lazy"
                        decoding="async" onError={hideOnError}
                      />
                    </div>
                    {/* Text rechts */}
                    <div className="flex h-full flex-col justify-center">
                      <div>
                        <h3 className="heading-h3 text-brand-wahlen">
                          {card.title}
                        </h3>
                        <p className="mt-4 font-body text-base leading-relaxed text-foreground/85">
                          {card.text}
                        </p>
                      </div>
                      <div className="mt-6 font-display text-sm font-extrabold tracking-wider text-brand-wahlen/60">
                        Grund {i + 1} von {gruende.length}
                      </div>
                    </div>
                  </article>
                </li>
              );
            })}
          </ol>


        </div>
      </section>


      {/* Ticket W14: Freebie als zweiter Weg, unmittelbar vor den Stimmen. */}
      <FreebieTeaserSection
        slug="musterrede-buergermeisterwahl"
        kennung="buergermeisterwahl-musterreden"
        accentClass="text-brand-wahlen"
        heading="Kostenlos: Fünf Musterreden für Ihren Wahlkampf."
        text="Von der Kandidatenvorstellung bis zum Wahlabend. Fünf Reden aus echten Wahlkämpfen, anonymisiert und aufbereitet, als PDF zum Herunterladen. Ohne Kosten, ohne Verpflichtung."
      />

      {/* Testimonials – nur Bürgermeister */}
      <TestimonialsSlider
        fixedCategory="buergermeisterwahl"
        showFilters={false}
        headingColor="text-brand-wahlen"
        title={
          <>
            Stimmen aus dem{" "}
            <span className="font-display font-extrabold">Rathaus.</span>
          </>
        }
      />

      {/* Häufige Fragen */}
      <ContactFaqSection
        faqs={BUERGERMEISTER_FAQS}
        headingId="buergermeisterwahl-faq"
        headingClassName="text-brand-wahlen"
      />

      {/* Prozess */}
      <section aria-labelledby="prozess">
        <div className="section-y mx-auto w-full max-w-[1400px] px-6 md:px-12">
          <Reveal>
            <h2
              id="prozess"
              className="heading-h2 text-brand-wahlen"
            >
              In drei Schritten{" "}
              zur Zusammenarbeit.
            </h2>
          </Reveal>

          <div className="mt-16 md:mt-20">
            <ProzessTimeline />
          </div>

          <Reveal>
            <p className="mt-16 font-body text-sm leading-relaxed text-foreground/80 md:text-base">
              Bei umfangreichen Oberbürgermeisterwahlkämpfen ab 100.000 Einwohnern bietet sich ein Gespräch vor Ort an. Kommen Sie hierfür gerne per E-Mail auf uns zu.
            </p>
          </Reveal>
        </div>
      </section>

      <ContactCta />

    </Layout>
  );
}
